/**
 * Avviso "nuova versione" in basso a destra.
 *
 * Perche' esiste: a ogni deploy il server rifa' la cartella dei file e quelli
 * della versione precedente spariscono. Una scheda rimasta aperta continua a
 * girare col codice vecchio e, al primo cambio pagina, chiede un file che non
 * c'e' piu': schermata bianca. Con il service worker in modalita' "chiedi"
 * (vite.config.ts) la versione vecchia resta servita dalla sua cache e qui
 * compare l'avviso: l'utente ricarica quando gli fa comodo.
 *
 * Il controllo della nuova versione lo fa il browser quando la scheda torna in
 * primo piano e, per chi sta col CRM aperto tutto il giorno, ogni 15 minuti.
 */
import { RefreshCw, X } from 'lucide-react';
import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { rimuoviVecchioServiceWorkerPush } from '../../lib/pwa/serviceWorkerUnico';

const INTERVALLO_CONTROLLO_MS = 15 * 60 * 1000;

/** Una sola pianificazione anche se il componente viene rimontato. */
let controlloAvviato = false;

function avviaControlloPeriodico(registrazione: ServiceWorkerRegistration) {
  if (controlloAvviato) return;
  controlloAvviato = true;
  const controlla = () => {
    if (document.hidden || !navigator.onLine) return;
    registrazione.update().catch(() => undefined);
  };
  window.setInterval(controlla, INTERVALLO_CONTROLLO_MS);
  window.addEventListener('focus', controlla);
}

/**
 * Ricarica anche quando il service worker degli aggiornamenti non c'e' piu'.
 *
 * updateServiceWorker() parla al service worker "in attesa" e ricarica la
 * pagina quando questo prende il controllo. Ma se il controllo e' passato al
 * service worker delle notifiche (registrato sullo stesso indirizzo di base,
 * quindi sostituisce l'altro) non c'e' nessuno in attesa: il messaggio non
 * arriva a nessuno, la pagina non si ricarica e il pulsante sembra morto.
 * Qui, se dopo un attimo siamo ancora sulla stessa pagina, svuotiamo le cache
 * dei file e ricarichiamo a mano.
 */
async function ricaricaDavvero(aggiorna: (reload: boolean) => Promise<void>) {
  try {
    await aggiorna(true);
  } catch {
    // il service worker non ha risposto: si prosegue con la ricarica manuale
  }

  window.setTimeout(async () => {
    try {
      const chiavi = await caches.keys();
      await Promise.all(chiavi.map((chiave) => caches.delete(chiave)));
    } catch {
      // se le cache non si possono toccare, la ricarica basta comunque
    }
    window.location.reload();
  }, 1200);
}

export function UpdateNotification() {
  // All'avvio si toglie di mezzo la vecchia registrazione autonoma delle
  // notifiche: finche' resta, e' lei a controllare la pagina e gli
  // aggiornamenti non arrivano piu'.
  useEffect(() => { void rimuoviVecchioServiceWorkerPush(); }, []);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registrazione) {
      if (registrazione) avviaControlloPeriodico(registrazione);
    },
    onRegisterError(error) {
      console.error('Errore registrazione Service Worker:', error);
    },
  });

  if (!needRefresh) return null;

  return (
    <div className="fixed bottom-20 right-4 z-[9998] w-[min(340px,calc(100vw-2rem))] sm:bottom-4">
      <div className="animate-fade-scale-in rounded-lg border border-[rgba(55,53,47,0.12)] bg-white p-3 shadow-lg dark:border-[#3F4447] dark:bg-[#2F3437]">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-[rgba(233,30,140,0.10)] text-[#E91E8C]">
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-[#37352F] dark:text-white">
              Nuova versione disponibile
            </div>
            <p className="mt-0.5 text-[12px] leading-snug text-[#787774] dark:text-gray-400">
              Ricarica per averla. Il lavoro non salvato va perso, quindi fallo quando ti torna comodo.
            </p>
            <div className="mt-2 flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => { void ricaricaDavvero(updateServiceWorker); }}
                className="rounded-md bg-[#37352F] px-2.5 py-1 text-[12px] font-medium text-white transition-colors hover:bg-black dark:bg-white dark:text-[#191919] dark:hover:bg-gray-200"
              >
                Ricarica
              </button>
              <button
                type="button"
                onClick={() => setNeedRefresh(false)}
                className="rounded-md px-2.5 py-1 text-[12px] text-[#787774] transition-colors hover:bg-[rgba(55,53,47,0.06)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
              >
                Più tardi
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setNeedRefresh(false)}
            title="Chiudi"
            className="flex-shrink-0 rounded p-0.5 text-[rgba(55,53,47,0.4)] transition-colors hover:bg-[rgba(55,53,47,0.08)] hover:text-[#37352F] dark:text-gray-500 dark:hover:bg-white/[0.08] dark:hover:text-white"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
